import React, { useContext, useMemo } from 'react';

import classnames from 'classnames';

import {
  loadMoreActionItems,
  removeItem as removeActionItem,
} from 'common/actions/actionItemQueries';
import { reloadBoard } from 'common/actions/boards';
import { invalidateDashboardActivity } from 'common/actions/dashboardActivity';
import { deletePostDraft, savePostDraft } from 'common/actions/postDraft';
import { reloadOnMerge } from 'common/actions/postMerge';
import { invalidatePostQueries } from 'common/actions/postQueries';
import { reloadPost } from 'common/actions/posts';
import { reloadPostActivity } from 'common/actions/postsActivity';
import { invalidateSuggestions as invalidatePostSuggestions } from 'common/actions/postSuggestions';
import {
  type QueryParams,
  invalidateQueriesAndReload,
  loadMoreQueueItems,
  removeItem as removeQueueItem,
} from 'common/actions/queueItemQueries';
import { invalidateRoadmap } from 'common/actions/roadmap';
import {
  reloadRoadmapPostsForRoadmapsWithPost,
  removePostFromAllRoadmaps,
} from 'common/actions/roadmapPosts';
import AJAX from 'common/AJAX';
import {
  AutopilotActionType,
  isDraftActionItem,
  isIdeaActionItem,
  isInsightActionItem,
  isPostActionItem,
} from 'common/constants/autopilotActions';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import RoadmapPostContainer from 'common/containers/RoadmapPostContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import ModalPortal from 'common/modals/ModalPortal';
import SelectableDataTable from 'common/SelectableDataTable';
import AdminCreatePostModal from 'common/subdomain/admin/AdminCreatePostModal';
import AdminFeedbackPost from 'common/subdomain/admin/AdminFeedbackPost';
import { isNotNil } from 'common/util/isNil';
import mapify from 'common/util/mapify';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';

import AutopilotNUX from './AutopilotNUX';
import {
  AuditLogActionsCell,
  AuditLogSuggestionCell,
  GenericSourceCell,
  QueueActionsCell,
  QueueSuggestionCell,
} from './DataTableCells';

import type {
  ActionItemsState,
  Counts,
  InboxCannyPostItem,
  InboxDraftItem,
  QueueInboxItem,
  QueueItemsState,
} from './types';
import type { Board } from 'common/api/endpoints/boards';
import type { Company } from 'common/api/endpoints/companies';
import type { ActionItem, PostActionItem, PostDraftActionItem } from 'common/api/endpoints/queue';
import type { Roadmap } from 'common/api/endpoints/roadmaps';
import type { CustomPostField } from 'common/api/resources/postFields';
import type { Post } from 'common/api/resources/posts';
import type { Source } from 'common/constants/sources';
import type { Column } from 'common/DataTable';
import type { Dispatch } from 'redux-connect';

import 'css/components/subdomain/admin/_AdminQueuePage.scss';

enum Variant {
  Action = 'Action',
  Suggestion = 'Suggestion',
}

interface BaseRow {
  id: string;
  created: Date;
}

interface ActionRow extends BaseRow {
  variant: Variant.Action;
  item: ActionItem;
  source: string | Source;
  actions: {
    create: () => Promise<void>;
    delete: () => Promise<void>;
    merge: (post: Post) => Promise<void>;
  };
}

interface SuggestionRow extends BaseRow {
  variant: Variant.Suggestion;
  item: QueueInboxItem;
  source: string;
  actions: {
    approve: () => Promise<void>;
    delete: () => Promise<void>;
    merge: (post: Post) => Promise<void>;
  };
}

type CombinedRow = SuggestionRow | ActionRow;

type ConnectProps = {
  boards: Board[];
  customPostFields: CustomPostField[];
  deletePostDraft: () => void;
  loadMoreActionItems: (queryParams: QueryParams, pages: number) => Promise<void>;
  loadMoreQueueItems: (queryParams: QueryParams, pages: number) => void;
  queueItemCounts: Counts;
  queueItemList: QueueItemsState;
  actionItemCounts: Counts;
  actionItemList: ActionItemsState;
  removeActionItem: (item: ActionItem) => void;
  removeQueueItem: (item: QueueInboxItem) => void;
  removePostWithID: (postID: string, board: Board) => Promise<void[]>;
  reloadOnMerge: (mergePost: Post, intoPost: Post, fromBoard: Board, toBoard: Board) => void;
  reloadPost: (post: ReloadPost, queryParams?: QueryParams) => Promise<unknown[]>;
  reloadPostActivity: (post: ReloadPost) => Promise<unknown[]>;
  roadmaps: Roadmap[];
  savePostDraft: (draft: {
    title: string;
    details: string | null;
    internalComment: string | null;
    author: any;
  }) => void;
};

type OwnProps = Record<string, never>;
type Props = ConnectProps & OwnProps;

const revertAutopilotAction = async (item: ActionItem) => {
  const response = await AJAX.post('/api/queue/actions/revert', {
    actionID: item._id,
  });

  return parseAPIResponse(response, {
    errors: {
      'not authorized': 'You are not authorized to revert this action',
      default: 'There was a problem reverting this action. Try again.',
    },
    isSuccessful: isDefaultSuccessResponse,
  });
};

const deletePost = async (postID: string) => {
  const response = await AJAX.post('/api/posts/delete', {
    postID,
  });

  return parseAPIResponse(response, {
    isSuccessful: isDefaultSuccessResponse,
  });
};

const mergePosts = async (item: PostActionItem, targetPost: Post) => {
  const response = await AJAX.post('/api/posts/merge', {
    mergePostID: item.sourceID,
    mergePostIntoID: targetPost._id,
  });

  return parseAPIResponse(response, {
    isSuccessful: isDefaultSuccessResponse,
    errors: {
      'board deleted': 'The board for one of the posts does not exist anymore',
      'invalid company': "You can't merge posts from another company",
      'not authorized': "You don't have permission to merge posts",
      'post missing': 'One of the posts you are trying to merge does not exist anymore',
      'same posts': 'It is not possible to merge one post with itself',
    },
  });
};

const getQueueItemCreated = (inboxItem: QueueInboxItem): Date => {
  const createdISO =
    inboxItem.type === 'draft' ? inboxItem.createdAt : inboxItem.created || inboxItem.updatedAt;
  return createdISO ? new Date(createdISO) : new Date();
};

const getActionItemCreated = (item: ActionItem): Date => new Date(item.createdAt);

const AdminQueueUnified = ({
  boards,
  customPostFields,
  deletePostDraft,
  loadMoreActionItems,
  loadMoreQueueItems,
  queueItemCounts,
  queueItemList,
  actionItemCounts,
  actionItemList,
  removeActionItem,
  removeQueueItem,
  reloadOnMerge,
  reloadPost,
  reloadPostActivity,
  removePostWithID,
  roadmaps,
  savePostDraft,
}: Props) => {
  const company = useContext<Company>(CompanyContext);
  const showToast = useContext(ShowToastContext);
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);
  const openModal = useContext(OpenModalContext);

  const convertQueueItemToRow = (inboxItem: QueueInboxItem): SuggestionRow | null => {
    const createdISO =
      inboxItem.type === 'draft' ? inboxItem.createdAt : inboxItem.created || inboxItem.updatedAt;
    const created = createdISO ? new Date(createdISO) : new Date();

    const onApproveDraft = async (
      item: InboxDraftItem,
      post: Post,
      { addVote }: { addVote: boolean },
    ) => {
      const response = await AJAX.post('/api/queue/drafts/approve', {
        postDraftID: item._id,
        postID: post._id,
        addVote,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          default: 'There was a problem approving this item. Try again.',
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      removeQueueItem(item);
      reloadPostActivity(post);
    };

    const onDeleteDraft = async (item: InboxDraftItem) => {
      const response = await AJAX.post('/api/queue/drafts/reject', {
        postDraftID: item._id,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          default: 'There was a problem rejecting this item. Try again.',
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      removeQueueItem(item);
    };

    const approveDraft = async (item: InboxDraftItem) => {
      deletePostDraft();
      savePostDraft({
        title: item.title,
        details: item.details,
        internalComment: item.details,
        author: item.author,
      });

      openModal(AdminCreatePostModal, {
        boards,
        onUnmount: () => deletePostDraft(),
        onPostCreated: async (post: Post) => {
          await onApproveDraft(item, post, { addVote: false });
          showToast('Post created', ToastTypes.success);
        },
        onVoteAdded: async (post: Post) => {
          await onApproveDraft(item, post, { addVote: true });
          showToast('Vote added', ToastTypes.success);
        },
      });
    };

    const deleteDraft = async (item: InboxDraftItem) => {
      await onDeleteDraft(item);
      showToast('Dismissed', ToastTypes.success);
    };

    const onMergeDraft = async (item: InboxDraftItem, post: Post) => {
      await onApproveDraft(item, post, { addVote: true });
      showToast('Merged as vote', ToastTypes.success);
    };

    const onRejectPost = async (item: InboxCannyPostItem) => {
      const response = await AJAX.post('/api/queue/posts/reject', {
        postID: item._id,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          default: 'There was a problem rejecting the suggestion. Try again.',
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      removeQueueItem(item);
      showToast('Post rejected', ToastTypes.success);
    };

    const onApprovePost = async (item: InboxCannyPostItem, mergeIntoPost?: Post) => {
      const response = await AJAX.post('/api/queue/posts/approve', {
        postID: item._id,
        mergeIntoPostID: mergeIntoPost?._id,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          default: 'There was a problem approving the suggestion. Try again.',
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      removeQueueItem(item);
      showToast('Post approved', ToastTypes.success);
    };

    const onDeletePost = async (item: InboxCannyPostItem) => {
      const { error } = await deletePost(item._id);
      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      removeQueueItem(item);
      removePostWithID(item._id, item.board);
      showToast('Post deleted', ToastTypes.success);
    };

    const onMergePost = async (item: InboxCannyPostItem, targetPost: Post) => {
      const targetBoard = boards.find((board) => board._id === targetPost.boardID);
      if (!targetBoard) {
        showToast('Failed to find the target board', ToastTypes.error);
        return;
      }

      await onApprovePost(item, targetPost);

      const response = await AJAX.post('/api/posts/merge', {
        mergePostID: item._id,
        mergePostIntoID: targetPost._id,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          'board deleted': 'The board for one of the posts does not exist anymore',
          'invalid company': "You can't merge posts from another company",
          'not authorized': "You don't have permission to merge posts",
          'post missing': 'One of the posts you are trying to merge does not exist anymore',
          'same posts': 'It is not possible to merge one post with itself',
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      reloadOnMerge(item, targetPost, item.board, targetBoard);
      showToast('Posts merged', ToastTypes.success);
    };

    const commonParams = {
      variant: Variant.Suggestion,
      id: inboxItem._id,
      source: inboxItem.source,
      created,
      item: inboxItem,
    } as const;

    if (inboxItem.type === 'draft') {
      return {
        ...commonParams,
        actions: {
          approve: () => approveDraft(inboxItem),
          delete: () => deleteDraft(inboxItem),
          merge: (post: Post) => onMergeDraft(inboxItem, post),
        },
      };
    }

    const postItem = inboxItem as InboxCannyPostItem;
    if (!postItem.duplicatePost) {
      return {
        ...commonParams,
        actions: {
          approve: () => onApprovePost(postItem),
          delete: () => onDeletePost(postItem),
          merge: (post: Post) => onMergePost(postItem, post),
        },
      };
    }

    return {
      ...commonParams,
      actions: {
        approve: () => onApprovePost(postItem),
        delete: () => onRejectPost(postItem),
        merge: (post: Post) => onMergePost(postItem, post),
      },
    };
  };

  const revertActionAndReload = async (item: ActionItem) => {
    const { error } = await revertAutopilotAction(item);

    if (error) {
      showToast(error.message, ToastTypes.error);
      return { success: false };
    }

    removeActionItem(item);

    const isPortalActionItem = isPostActionItem(item) || isDraftActionItem(item);
    if (!isPortalActionItem) {
      return { success: true };
    }

    if (item.createdPostID && item.board) {
      removePostWithID(item.createdPostID, item.board);
    }
    if (item.duplicatePost) {
      reloadPostActivity(item.duplicatePost);
    }

    return { success: true };
  };

  const convertActionItemToRow = (item: ActionItem): ActionRow => {
    const created = new Date(item.createdAt);

    const deleteSourcePost = async (postItem: PostActionItem) => {
      const { error } = await deletePost(postItem.sourceID);
      if (error) {
        showToast(
          'Failed to delete source post. You may have to delete this post manually.',
          ToastTypes.error,
        );
        return;
      }
    };

    const onPostMerge = async (postItem: PostActionItem, targetPost: Post) => {
      const boardsMap = mapify(boards, '_id');
      const targetBoard = boardsMap[targetPost.boardID];
      if (!targetBoard) {
        showToast('Failed to find the target board. Try again', ToastTypes.error);
        return;
      }

      const { error } = await mergePosts(postItem, targetPost);
      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      await reloadPostActivity({ ...targetPost, board: targetBoard });
      removeActionItem(postItem);
    };

    const onApproveDraft = async (
      draftItem: PostDraftActionItem,
      post: Post,
      { addVote }: { addVote: boolean },
    ) => {
      const response = await AJAX.post('/api/queue/drafts/approve', {
        postDraftID: draftItem.sourceID,
        postID: post._id,
        addVote,
      });

      const { error } = parseAPIResponse(response, {
        isSuccessful: isDefaultSuccessResponse,
        errors: {
          default: `There was a problem ${addVote ? 'adding the vote' : 'creating the post'}. Try again.`,
        },
      });

      if (error) {
        showToast(error.message, ToastTypes.error);
        return;
      }

      await revertActionAndReload(draftItem);
      await reloadPostActivity(post);

      if (addVote) {
        showToast('Vote added', ToastTypes.success);
      } else {
        showToast('Post created', ToastTypes.success);
      }
    };

    const common = {
      variant: Variant.Action,
      id: item._id,
      source: item.source,
      created,
      item,
    } as const;

    if (isDraftActionItem(item)) {
      return {
        ...common,
        actions: {
          create: async () => {
            deletePostDraft();
            savePostDraft({
              title: item.sourcePostDraft.title,
              details: item.sourcePostDraft.details,
              internalComment: item.sourcePostDraft.details,
              author: item.sourcePostDraft.author,
            });
            openModal(AdminCreatePostModal, {
              boards,
              onUnmount: () => deletePostDraft(),
              onPostCreated: async (post: Post) => onApproveDraft(item, post, { addVote: false }),
              onVoteAdded: async (post: Post) => onApproveDraft(item, post, { addVote: true }),
            });
          },
          merge: async (post: Post) => onApproveDraft(item, post, { addVote: true }),
          delete: async () => {
            const { success } = await revertActionAndReload(item);
            if (!success) {
              return;
            }
            if (item.duplicatePost) {
              showToast('Vote deleted', ToastTypes.success);
            } else {
              showToast('Post deleted', ToastTypes.success);
            }
          },
        },
      };
    }

    if (isPostActionItem(item)) {
      return {
        ...common,
        actions: {
          create: async () => {
            const { success } = await revertActionAndReload(item);
            if (!success) {
              return;
            }
            showToast('Post created', ToastTypes.success);
          },
          merge: async (post: Post) => {
            const { success } = await revertActionAndReload(item);
            if (!success) {
              return;
            }
            await onPostMerge(item, post);
            showToast('Posts merged', ToastTypes.success);
          },
          delete: async () => {
            const { success } = await revertActionAndReload(item);
            if (!success) {
              return;
            }
            await deleteSourcePost(item);
            showToast('Post deleted', ToastTypes.success);
          },
        },
      };
    }

    if (isInsightActionItem(item)) {
      return {
        ...common,
        actions: {
          create: async () => {},
          merge: async () => {},
          delete: async () => {
            const { success } = await revertActionAndReload(item);
            if (!success) {
              return;
            }
            showToast('Insight deleted', ToastTypes.success);
          },
        },
      };
    }

    if (isIdeaActionItem(item)) {
      return {
        ...common,
        actions: {
          create: async () => {},
          merge: async () => {},
          delete: async () => {
            const { success } = await revertActionAndReload(item);
            if (!success) {
              return;
            }
            if (item.actionType === AutopilotActionType.dismiss) {
              showToast('Idea restored', ToastTypes.success);
            } else if (item.actionType === AutopilotActionType.create) {
              showToast('Idea deleted', ToastTypes.success);
            } else if (item.actionType === AutopilotActionType.merge) {
              showToast('Merge undone', ToastTypes.success);
            }
          },
        },
      };
    }

    // Fallback
    return {
      ...common,
      actions: {
        delete: async () => {},
        merge: async () => {},
        create: async () => {},
      },
    };
  };

  const rows: CombinedRow[] = useMemo(() => {
    const order = (location.query.sortDirection as 'asc' | 'desc' | undefined) || 'desc';

    // Compute per-list created-at extrema to decide a safe render cutoff that prevents
    // mid-list insertions when pages from different sources arrive at different times.
    const queueCreateds = queueItemList.items.map(getQueueItemCreated).map((d) => d.getTime());
    const actionCreateds = actionItemList.items.map(getActionItemCreated).map((d) => d.getTime());

    const [queueMin, queueMax] = queueCreateds.length
      ? [Math.min(...queueCreateds), Math.max(...queueCreateds)]
      : [null, null];
    const [actionMin, actionMax] = actionCreateds.length
      ? [Math.min(...actionCreateds), Math.max(...actionCreateds)]
      : [null, null];

    // For asc (oldest first): only render items with created <= min(newestLoaded per list)
    // For desc (newest first): only render items with created >= max(oldestLoaded per list)
    const hasQueueBound = queueMin !== null && queueMax !== null;
    const hasActionBound = actionMin !== null && actionMax !== null;
    const canApplySafeCutoff = hasQueueBound && hasActionBound;
    const safeCutoff: Date | null = canApplySafeCutoff
      ? order === 'asc'
        ? new Date(Math.min(queueMax!, actionMax!))
        : new Date(Math.max(queueMin!, actionMin!))
      : null;

    const queueHasMore = queueItemList.hasMore;
    const actionHasMore = actionItemList.hasMore;

    const passesSafeCutoff = (r: CombinedRow) => {
      // When both lists can still paginate, use the conservative global cutoff
      if (queueHasMore && actionHasMore && safeCutoff) {
        return order === 'asc' ? r.created <= safeCutoff : r.created >= safeCutoff;
      }

      // Otherwise, relax per list so exhausted sources don't block older items
      if (order === 'desc') {
        if (r.variant === Variant.Action) {
          // Only gate actions against queue if queue can still load more
          if (queueHasMore && queueMin !== null) {
            return r.created.getTime() >= queueMin;
          }
          return true;
        }
        // Suggestion rows
        if (actionHasMore && actionMin !== null) {
          return r.created.getTime() >= actionMin;
        }
        return true;
      }

      // Ascending (oldest first)
      if (r.variant === Variant.Action) {
        if (queueHasMore && queueMax !== null) {
          return r.created.getTime() <= queueMax;
        }
        return true;
      }
      if (actionHasMore && actionMax !== null) {
        return r.created.getTime() <= actionMax;
      }
      return true;
    };

    const queueRows = queueItemList.items
      .map(convertQueueItemToRow)
      .filter(isNotNil)
      .filter(passesSafeCutoff);

    const actionRows = actionItemList.items
      .map(convertActionItemToRow)
      .filter(isNotNil)
      .filter(passesSafeCutoff);

    const combined = [...queueRows, ...actionRows];
    combined.sort((a, b) =>
      order === 'asc'
        ? a.created.getTime() - b.created.getTime()
        : b.created.getTime() - a.created.getTime(),
    );
    return combined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueItemList.items, actionItemList.items, location.query.sortDirection]);

  const isLoading = queueItemList.loading || actionItemList.loading;
  const isError = queueItemList.error || actionItemList.error;

  const getSelectedPost = () => {
    if (!location.query.postURLName) {
      return;
    }

    const queuePotentialPosts = queueItemList.items
      .map((item) => {
        if (item.source === 'canny' && item.duplicatePost) {
          return [item, item.duplicatePost];
        } else if (item.source === 'canny') {
          return item;
        } else if (item.duplicatePost) {
          return item.duplicatePost;
        }
        return null;
      })
      .flat()
      .filter(isNotNil);

    const actionPotentialPosts = actionItemList.items
      .map((item) => {
        const isPortal = isPostActionItem(item) || isDraftActionItem(item);
        if (!isPortal) {
          return null;
        }

        if (item.source === 'canny' && item.duplicatePost) {
          return [item.sourcePost, item.duplicatePost];
        } else if (item.duplicatePost) {
          return item.duplicatePost;
        } else if (item.createdPost) {
          return item.createdPost;
        }

        return null;
      })
      .flat()
      .filter(isNotNil);

    const potentialPosts = [...queuePotentialPosts, ...actionPotentialPosts];
    return potentialPosts.find((post) => post.urlName === location.query.postURLName);
  };

  const selectedPost = getSelectedPost();
  const closePost = () =>
    router.replace({
      pathname: location.pathname,
      query: { ...location.query, postURLName: undefined },
    });

  if (!isLoading && !isError && rows.length === 0) {
    // merge counts
    const counts: Counts = {
      totalCount: queueItemCounts.totalCount + actionItemCounts.totalCount,
      cannyCount: queueItemCounts.cannyCount + actionItemCounts.cannyCount,
      totalSourceCount: queueItemCounts.totalSourceCount + actionItemCounts.totalSourceCount,
      // combine counts for the same source from both queue and action items
      perSourceCounts: [
        ...Object.entries(queueItemCounts.perSourceCounts),
        ...Object.entries(actionItemCounts.perSourceCounts),
      ].reduce(
        (acc, [source, count]) => {
          if (!acc[source]) {
            acc[source] = 0;
          }
          acc[source] += count;
          return acc;
        },
        {} as Partial<Record<string, number>>,
      ),
    };

    return (
      <div className="adminQueuePage empty">
        <AutopilotNUX company={company} counts={counts} />
      </div>
    );
  }

  const columns: Column<CombinedRow, any>[] = [
    {
      id: 'source',
      align: 'left',
      header: 'Source',
      sortable: false,
      cell: (_, { item, variant }) => {
        if (variant === Variant.Action) {
          if (isDraftActionItem(item)) {
            return (
              <GenericSourceCell
                source={item.source}
                sourceType="draft"
                link={item.link}
                hasDuplicatePost={!!item.duplicatePost}
              />
            );
          } else if (isPostActionItem(item)) {
            return (
              <GenericSourceCell
                hasDuplicatePost
                sourceType="post"
                urlName={item.duplicatePost.urlName}
              />
            );
          } else if (isIdeaActionItem(item)) {
            return <GenericSourceCell sourceType="idea" source={item.source} />;
          } else if (isInsightActionItem(item)) {
            return <GenericSourceCell sourceType="insight" source={item.source} />;
          }

          return null;
        } else if (variant === Variant.Suggestion) {
          if (item.type === 'draft') {
            return (
              <GenericSourceCell
                hasDuplicatePost={!!item.duplicatePost}
                link={item.link}
                source={item.source}
                sourceType="draft"
              />
            );
          } else if (item.type === 'post') {
            return (
              <GenericSourceCell
                hasDuplicatePost={!!item.duplicatePost}
                sourceType="post"
                urlName={item.urlName}
              />
            );
          }

          return null;
        }

        return null;
      },
    } as Column<CombinedRow, 'source'>,
    {
      id: 'item',
      align: 'left',
      header: 'Suggestion',
      fullWidth: true,
      sortable: false,
      cell: (_, row) =>
        row.variant === Variant.Action ? (
          <AuditLogSuggestionCell company={company} item={row.item} />
        ) : (
          <QueueSuggestionCell boards={boards} company={company} item={row.item} />
        ),
    } as Column<CombinedRow, 'item'>,
    {
      id: 'actions',
      align: 'left',
      header: 'Actions',
      sortable: false,
      cell: (_, row) =>
        row.variant === Variant.Action ? (
          <AuditLogActionsCell boards={boards} row={row} />
        ) : (
          <QueueActionsCell boards={boards} row={row} />
        ),
    } as Column<CombinedRow, 'actions'>,
  ].filter(isNotNil);

  const onLoadMore = async () => {
    const tasks: Array<Promise<unknown>> = [];
    if (queueItemList.hasMore && !queueItemList.loadingMore) {
      tasks.push(
        Promise.resolve(
          loadMoreQueueItems(
            queueItemList.queryParams,
            queueItemList.pages ? queueItemList.pages + 1 : 2,
          ),
        ),
      );
    }
    if (actionItemList.hasMore && !actionItemList.loadingMore) {
      tasks.push(
        loadMoreActionItems(
          actionItemList.queryParams,
          actionItemList.pages ? actionItemList.pages + 1 : 2,
        ),
      );
    }
    if (tasks.length) {
      await Promise.all(tasks);
    }
  };

  return (
    <div className="adminQueuePage">
      <SelectableDataTable<CombinedRow>
        fullHeight
        className="adminQueueTable"
        cellAlignment="middle"
        loading={isLoading}
        error={isError}
        padding="small"
        rounded={false}
        pagination={{
          hasMore: queueItemList.hasMore || actionItemList.hasMore,
          loading: queueItemList.loadingMore || actionItemList.loadingMore,
          onLoadMore,
        }}
        // TODO: implement keyboard shortcuts
        hotkeys={{}}
        columns={columns}
        rows={rows}
      />
      {selectedPost && (
        <ModalPortal
          allowBodyScroll
          closeOnClickAway={false}
          allowEscape
          className={classnames('adminQueueDrawer', 'postModal', 'modalVisible')}
          onClose={closePost}>
          <div className="modalContents">
            <RoadmapPostContainer boards={boards} post={selectedPost}>
              <AdminFeedbackPost
                key={selectedPost?._id}
                customPostFields={customPostFields}
                onClose={closePost}
                onDelete={() => {
                  if (!selectedPost || !selectedPost.board) {
                    return;
                  }
                  removePostWithID(selectedPost._id, selectedPost.board);
                  closePost();
                }}
                onEdited={() => {
                  if (!selectedPost) {
                    return;
                  }
                  // TODO: check if we actually need to add extra data to the post to reload it for the action items
                  reloadPost(selectedPost, queueItemList.queryParams);
                  reloadPostActivity(selectedPost);
                  closePost();
                }}
                roadmaps={roadmaps}
                skipFixURL={true}
              />
            </RoadmapPostContainer>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

type ReloadPost = { _id: string; urlName: string; board: Board };

export default connect(null, (dispatch: Dispatch) => ({
  deletePostDraft: () => dispatch(deletePostDraft()),
  loadMoreQueueItems: (queryParams: QueryParams, pages: number) =>
    dispatch(loadMoreQueueItems(queryParams, pages)),
  loadMoreActionItems: (queryParams: QueryParams, pages: number) =>
    dispatch(loadMoreActionItems(queryParams, pages)),
  savePostDraft: (postDraft: any) => dispatch(savePostDraft(postDraft)),
  reloadOnMerge: (mergePost: Post, intoPost: Post, fromBoard: Board, toBoard: Board) =>
    dispatch(reloadOnMerge(mergePost, intoPost, fromBoard, toBoard)),
  reloadPostActivity: (post: ReloadPost) =>
    Promise.all([dispatch(reloadPostActivity(post)), dispatch(reloadPost(post))]),
  reloadPost: (post: ReloadPost, queryParams?: QueryParams) =>
    Promise.all([
      dispatch(invalidatePostQueries()),
      dispatch(reloadPost(post)),
      queryParams && dispatch(invalidateQueriesAndReload(queryParams)),
      dispatch(reloadRoadmapPostsForRoadmapsWithPost(post)),
    ]),
  removePostWithID: (postID: string, board: Board) =>
    Promise.all([
      dispatch(reloadBoard(board.urlName)),
      dispatch(invalidateDashboardActivity()),
      dispatch(invalidateRoadmap()),
      dispatch(invalidatePostSuggestions()),
      dispatch(removePostFromAllRoadmaps({ _id: postID })),
      dispatch(invalidatePostQueries()),
    ]),
  removeActionItem: (item: ActionItem) => dispatch(removeActionItem(item)),
  removeQueueItem: (item: QueueInboxItem) => dispatch(removeQueueItem(item)),
}))(AdminQueueUnified) as unknown as React.FC<OwnProps>;
